/*
 * AI 问照片 神策埋点统一封装(照片王商业化埋点 rid=1124,2026-06-11 改版)
 *
 * 事件覆盖(positionSign 见各段):
 *   - ai_b8112_exposure / click / completed   主面板 (aai.b8112)
 *   - ai_b8112_c8145_exposure / click         我的积分弹窗 (aai.b8112.c8145)
 *   - ai_b8112_c8146_exposure / click         积分不足弹窗 (aai.b8112.c8146)
 *   - ai_b8112_c8147_exposure / click         内测申请弹窗 (aai.b8112.c8147)
 *   - ai_b8112_c8132_exposure / click         下载生成任务二级页 (aai.b8112.c8132)
 *   - vip_b8148_exposure / click              积分加量包弹窗 (avip.b8148)
 *   - ai_b8113_exposure / click               新功能引导气泡 (旧需求 rid=1093)
 *
 * 设计准则:
 *   1. 每个事件一个独立函数, 调用方只需要传业务字段
 *   2. groupID / groupVipLevel / track_sign / requirement_id 在这里统一注入,
 *      调用方不感知; role 是 sensors registerPage 全局公共属性, 自动携带
 *   3. 拿不到的可选字段不要传 undefined, 按 dataType:
 *      - string: 默认 ''  - number: 默认 0  - list: 默认 []
 *      避免神策侧字段缺失告警
 *   4. 失败兜底: sensors.track 内部 try/catch (sa-sdk 本身就吞错),
 *      这里无需再 wrap
 */
import sensors from '../../../utils/sensors'
import { store } from '../../../app/store'
import CONFIG from '../../../constants/config'

/** 照片王商业化埋点需求(2026-06-11 平台登记, b8112 系列旧埋点改造也归入此需求) */
const REQUIREMENT_ID = 1124
/** 新功能引导气泡 b8113 仍属旧需求, 未随 1124 改版 */
const REQUIREMENT_ID_B8113 = 1093

const TRACK_SIGN_B8112_EXPOSURE = 'aai.b8112.exposure.ai_b8112_exposure'
const TRACK_SIGN_B8112_CLICK = 'aai.b8112.click.ai_b8112_click'
const TRACK_SIGN_B8112_COMPLETED = 'aai.b8112.completed.ai_b8112_completed'
const TRACK_SIGN_B8113_EXPOSURE = 'aai.b8113.exposure.ai_b8113_exposure'
const TRACK_SIGN_B8113_CLICK = 'aai.b8113.click.ai_b8113_click'
const TRACK_SIGN_C8145_EXPOSURE =
  'aai.b8112.c8145.exposure.ai_b8112_c8145_exposure'
const TRACK_SIGN_C8145_CLICK = 'aai.b8112.c8145.click.ai_b8112_c8145_click'
const TRACK_SIGN_C8146_EXPOSURE =
  'aai.b8112.c8146.exposure.ai_b8112_c8146_exposure'
const TRACK_SIGN_C8146_CLICK = 'aai.b8112.c8146.click.ai_b8112_c8146_click'
const TRACK_SIGN_C8147_EXPOSURE =
  'aai.b8112.c8147.exposure.ai_b8112_c8147_exposure'
const TRACK_SIGN_C8147_CLICK = 'aai.b8112.c8147.click.ai_b8112_c8147_click'
const TRACK_SIGN_C8132_EXPOSURE =
  'aai.b8112.c8132.exposure.ai_b8112_c8132_exposure'
const TRACK_SIGN_C8132_CLICK = 'aai.b8112.c8132.click.ai_b8112_c8132_click'
const TRACK_SIGN_B8148_EXPOSURE = 'avip.b8148.exposure.vip_b8148_exposure'
const TRACK_SIGN_B8148_CLICK = 'avip.b8148.click.vip_b8148_click'

/** 拿当前 group 的 vip_kind 文案; 与 sensors.js 内 sensorsVipLevel 等价(但那个未导出)。 */
const getVipLevel = (): string => {
  try {
    const state = store.getState() as any
    const groupJS = state?.group
    const isVip = groupJS?.isVip || groupJS?.is_vip
    if (!isVip) return 'nonvip'
    const vipKind = groupJS?.vipInfo?.vip_kind || groupJS?.vip_info?.vip_kind
    switch (vipKind) {
      case CONFIG.VIP_KIND.VIP:
        return 'vip'
      case CONFIG.VIP_KIND.SVIP:
        return 'svip'
      case CONFIG.VIP_KIND.VVIP:
        return 'vvip'
      case CONFIG.VIP_KIND.EVIP:
        return 'evip'
      default:
        return 'nonvip'
    }
  } catch {
    return ''
  }
}

const getGroupID = (): string => {
  try {
    const state = store.getState() as any
    const g = state?.group
    return g?.groupId || ''
  } catch {
    return ''
  }
}

/** 通用基础字段 (b8112 及其子位置 / b8148 公用, requirement_id 可按事件覆盖) */
const baseFields = (trackSign: string, requirementId = REQUIREMENT_ID) => ({
  groupID: getGroupID(),
  groupVipLevel: getVipLevel(),
  track_sign: trackSign,
  requirement_id: requirementId
})

/** 底部积分提示条 / 主面板曝光的用量状态 (16026/16027 string5 枚举) */
export type AiB8112UsageState = '满额未用' | '部分使用' | '已用90%' | '不足1次'

/** 我的积分弹窗的用量状态 (16028/16029 string5 枚举, 与 b8112 措辞不同, 照平台原文) */
export type AiC8145UsageState = '满额未用' | '正常使用' | '预警' | '不足1次'

/** PointsStatusCard 的 usageState → b8112 系 string5 文案 */
export const usageStateToB8112 = (
  s: 'unused' | 'normal' | 'warning' | 'critical'
): AiB8112UsageState => {
  switch (s) {
    case 'unused':
      return '满额未用'
    case 'warning':
      return '已用90%'
    case 'critical':
      return '不足1次'
    default:
      return '部分使用'
  }
}

/** PointsStatusCard 的 usageState → c8145 string5 文案 */
export const usageStateToC8145 = (
  s: 'unused' | 'normal' | 'warning' | 'critical'
): AiC8145UsageState => {
  switch (s) {
    case 'unused':
      return '满额未用'
    case 'warning':
      return '预警'
    case 'critical':
      return '不足1次'
    default:
      return '正常使用'
  }
}

// ==================== b8112 主面板 ====================

/**
 * ai_b8112_click 的 clickItem 枚举 (跟神策 spec rid=1093 trackId=15931 对齐)。
 *
 * 2026-05-15 更新: 神策侧把"点击积分"换成"hover积分", 加了"hover免费试用"。
 * 来源: GET /api/requirement/track?rid=1093&id=15931 中 properties[clickItem].rule
 *
 * 注:全集还包含 "历史会话 / 执行计划 / 下载与生成任务 / 关闭 / 点击上传附件 / 移除附件"
 * 等当前 panel 还没接的入口,需要时直接加。
 */
export type AiB8112ClickItem =
  // 跟 spec rid=1093 trackId=15931 clickItem 枚举严格对齐, 不在列表里的不要混进来 (神策侧会拒)
  | '点击发送'
  | '点击停止'
  | '开启新会话' // ← spec 用"开启",不是"点击新"
  | '点击试问'
  | '点击历史记录'
  | '点击继续追问'
  | '关闭' // ← spec 用"关闭",不是"点击关闭面板"
  | '点击卡片项'
  | '展开进度详情'
  | '收起进度详情'
  | '点赞'
  | '取消点赞'
  | '点踩'
  | '取消点踩'
  | '反馈问题'
  | '提交反馈'
  | '同意执行计划'
  | '调整执行计划'
  // 以下 spec 已定义但当前 UI 还没接的入口, 留作后续业务铺开使用
  | '历史会话'
  | '执行计划'
  | '下载与生成任务'
  | '点击上传附件'
  | '移除附件'
  // 2026-05-15 spec 新增: hover 触发, 替换原"点击积分"
  | 'hover积分'
  | 'hover免费试用'
  // 2026-06-10 spec(rid=1124) 新增: 底部积分提示条 CTA, 带"提示条-"前缀区分来源
  | '提示条-升级会员'
  | '提示条-增购积分'
  // 2026-06-23 spec 新增: onboarding 首屏轮播卡 / 输入框上方快捷按钮入口
  | '点击轮播卡片'
  | '点击快捷按钮'

export interface AiB8112ExposureParams {
  sessionID: string
  /** 历史记录 / "试试问我" 等业务数据 */
  list1?: string[]
  list2?: string[]
  /** "试试问我" 列表元素数量 */
  number1?: number
  /** 历史记录列表元素数量 */
  number2?: number
  /** 曝光时剩余积分 (从 wallet 拿) */
  state?: number
  exposureResult?: string
  /** 来源: 主页chip / 引导气泡 / 分享 / 路由跳转 ... */
  fromPlace?: string
  /** 团队照片总数 */
  photoNumber?: number
  /** 曝光时底部积分提示条的用量状态 (2026-06-10 spec 新增) */
  string5?: AiB8112UsageState
  /** 搜索画面引导卡是否曝光 (2026-06-10 spec 新增) */
  bool1?: boolean
  /** 本次曝光实际展示的 onboarding 轮播卡 cardLabel 数组 (2026-06-23 spec 新增; 没展示轮播卡时为空数组) */
  list3?: string[]
}

export const trackAiB8112Exposure = (params: AiB8112ExposureParams): void => {
  sensors.track('ai_b8112_exposure', {
    ...baseFields(TRACK_SIGN_B8112_EXPOSURE),
    sessionID: params.sessionID || '',
    list1: params.list1 ?? [],
    list2: params.list2 ?? [],
    number1: params.number1 ?? 0,
    number2: params.number2 ?? 0,
    state: params.state ?? 0,
    exposureResult: params.exposureResult ?? '',
    fromPlace: params.fromPlace ?? '',
    photoNumber: params.photoNumber ?? 0,
    string5: params.string5 ?? '',
    bool1: params.bool1 ?? false,
    list3: params.list3 ?? []
  })
}

export interface AiB8112ClickParams {
  clickItem: AiB8112ClickItem
  sessionID: string
  /** 关联到的 AI 回答消息 ID (点赞/点踩/反馈/卡片/进度/计划/继续追问 用) */
  msgID?: string
  /** clickItem=点击发送 时用户文本 */
  content?: string
  /** clickItem=点击试问 时具体内容 */
  string1?: string
  /** clickItem=点击历史记录 时具体内容 */
  string2?: string
  /** clickItem=点击继续追问 时具体内容 */
  string3?: string
  /** clickItem=提交反馈 时评论文本 */
  string4?: string
  /** clickItem=点击试问 时第几个 (1-based) */
  number1?: number
  /** clickItem=点击历史记录 时第几条 (1-based) */
  number2?: number
  /** clickItem=点击继续追问 时第几个 (1-based) */
  number3?: number
  /** clickItem=点击发送 时附件数量 */
  number4?: number
  /** 第几个 AI 回答消息 (1-based) */
  rankNum?: number
  /** clickItem=点击发送 时附件类型列表 (image / pdf / csv ...) */
  folderTypeList?: string[]
  /** clickItem=点击卡片项 时卡片类型 (中文枚举: 排行表/数值卡/html/PageFilterCard顶部chip/
   *  PageFilterCard下方缩略图/图片生成卡/搜索画面引导卡, 见 16026 typeName rule) */
  typeName?: string
  /** clickItem=同意/调整执行计划 时 plan.risk_level */
  warningLevel?: string
  /** clickItem=提示条-升级会员/提示条-增购积分 时底部提示条的用量状态 (2026-06-10 新增) */
  string5?: AiB8112UsageState
  /** clickItem=点击卡片项 时卡片内具体交互: 进入查看/预览/立即下载/重试/立即申请/打开地图
   *  (前五个 2026-06-10 新增; 打开地图 2026-06-23 spec 新增: typeName=排行表 / PageFilterCard下方缩略图 的「在地图上查看」) */
  place?: '进入查看' | '预览' | '立即下载' | '重试' | '立即申请' | '打开地图'
}

export const trackAiB8112Click = (params: AiB8112ClickParams): void => {
  sensors.track('ai_b8112_click', {
    ...baseFields(TRACK_SIGN_B8112_CLICK),
    clickItem: params.clickItem,
    sessionID: params.sessionID || '',
    msgID: params.msgID ?? '',
    content: params.content ?? '',
    string1: params.string1 ?? '',
    string2: params.string2 ?? '',
    string3: params.string3 ?? '',
    string4: params.string4 ?? '',
    number1: params.number1 ?? 0,
    number2: params.number2 ?? 0,
    number3: params.number3 ?? 0,
    number4: params.number4 ?? 0,
    rankNum: params.rankNum ?? 0,
    folderTypeList: params.folderTypeList ?? [],
    typeName: params.typeName ?? '',
    warningLevel: params.warningLevel ?? '',
    string5: params.string5 ?? '',
    place: params.place ?? ''
  })
}

/** b8112 spec sourceType 枚举 (16035 rule: 试试问我,历史记录,继续追问,手动输入,hover卡片-预设问题;
 *  2026-06-23 spec 新增 轮播卡片 / 快捷按钮)
 *  - 试试问我:  EmptyStateBlock DEFAULT_PRESETS 静态推荐
 *  - 历史记录: EmptyStateBlock recentQueries
 *  - 继续追问: 已有对话中再次发送 (含 plan 同意 / 问答卡反馈 / 用户在已开会话里手动输入)
 *  - 手动输入: 空会话下用户手动输入第一条
 *  - hover卡片-预设问题: 入口 hover 快捷卡片的预设问题发起
 *  - 轮播卡片 / 快捷按钮: onboarding 首屏轮播卡 / 输入框上方快捷按钮发起 (2026-06-23) */
export type AiB8112SourceType =
  | '试试问我'
  | '历史记录'
  | '继续追问'
  | '手动输入'
  | 'hover卡片-预设问题'
  | '轮播卡片'
  | '快捷按钮'

export interface AiB8112CompletedParams {
  sessionID: string
  msgID: string
  /** 后端 trace_id (SSE start 推过来的) */
  string1?: string
  /** 用户问题 */
  content?: string
  /** 完成结果 (平台 in 校验: 成功/失败/用户停止/超时; AI 回复正文放 string2) */
  result?: string
  errorMsg?: string
  /** 完成时剩余积分 */
  state?: number
  /** send→done 耗时 (ms) */
  costTime?: number
  /** 本次消耗积分 (后端 SSE done.cost_saving) */
  costSaving?: number
  /** 调用的工具列表 (按调用次数高到低) */
  list1?: string[]
  /** "你可以继续问" 列表 */
  list2?: string[]
  /** 工具调用次数 */
  number1?: number
  /** SSE round 数 (AI 思考轮数) */
  number2?: number
  /** 返回的结构化卡片数 */
  number3?: number
  /** "你可以继续问" 数量 */
  number4?: number
  /** 来源 (神策 spec 强制 enum, 不传/空字符串会被规则拦截) */
  sourceType?: AiB8112SourceType
}

export const trackAiB8112Completed = (params: AiB8112CompletedParams): void => {
  sensors.track('ai_b8112_completed', {
    ...baseFields(TRACK_SIGN_B8112_COMPLETED),
    sessionID: params.sessionID || '',
    msgID: params.msgID || '',
    string1: params.string1 ?? '',
    content: params.content ?? '',
    result: params.result ?? '',
    errorMsg: params.errorMsg ?? '',
    state: params.state ?? 0,
    costTime: params.costTime ?? 0,
    costSaving: params.costSaving ?? 0,
    list1: params.list1 ?? [],
    list2: params.list2 ?? [],
    number1: params.number1 ?? 0,
    number2: params.number2 ?? 0,
    number3: params.number3 ?? 0,
    number4: params.number4 ?? 0,
    sourceType: params.sourceType ?? ''
  })
}

// ==================== b8113 新功能引导气泡 ====================

export const trackAiB8113Exposure = (): void => {
  sensors.track(
    'ai_b8113_exposure',
    baseFields(TRACK_SIGN_B8113_EXPOSURE, REQUIREMENT_ID_B8113)
  )
}

// b8113 spec 枚举: '立即体验' | '关闭弹窗' (trackId=15926)
export type AiB8113ClickItem = '立即体验' | '关闭弹窗'

export const trackAiB8113Click = (clickItem: AiB8113ClickItem): void => {
  sensors.track('ai_b8113_click', {
    ...baseFields(TRACK_SIGN_B8113_CLICK, REQUIREMENT_ID_B8113),
    clickItem
  })
}

// ============ c8145 我的积分弹窗 (2026-06-11 spec, 替代旧 ai_b8131_* my-credits 场景) ============

export interface AiC8145ExposureParams {
  /** 当前会话 ID */
  sessionID: string
  /** 触发类型: CTA 展示的是哪种(超级/企业会员是增购积分包, 其余是升级会员) */
  trigger: '升级会员' | '增购积分包'
  /** 用量状态 */
  usageState: AiC8145UsageState
}

/** ai_b8112_c8145_exposure —— 我的积分弹窗曝光 */
export const trackAiC8145Exposure = (params: AiC8145ExposureParams): void => {
  sensors.track('ai_b8112_c8145_exposure', {
    ...baseFields(TRACK_SIGN_C8145_EXPOSURE),
    sessionID: params.sessionID || '',
    string1: params.trigger,
    string5: params.usageState
  })
}

export type AiC8145ClickItem =
  | '升级会员'
  | '增购积分包'
  | '联系客服'
  | '关闭弹窗'

export interface AiC8145ClickParams {
  sessionID: string
  clickItem: AiC8145ClickItem
  /** 用量状态 */
  usageState: AiC8145UsageState
}

/** ai_b8112_c8145_click —— 我的积分弹窗点击 */
export const trackAiC8145Click = (params: AiC8145ClickParams): void => {
  sensors.track('ai_b8112_c8145_click', {
    ...baseFields(TRACK_SIGN_C8145_CLICK),
    sessionID: params.sessionID || '',
    clickItem: params.clickItem,
    string5: params.usageState
  })
}

// ============ c8146 积分不足弹窗 (2026-06-11 spec, 替代旧 ai_b8131_* insufficient 场景) ============

/** 因何触发积分不足弹窗 = 触发本次查询的来源 (16030/16031 string1 rule 原文)。
 *  '下载文件' 为 2026-06 新增:下载文件扣积分时余额不足,复用本弹窗(需在神策平台补登记该枚举值)。 */
export type AiC8146Trigger =
  | '试试问我'
  | '历史记录'
  | '继续追问'
  | '手动输入发送消息'
  | 'hover卡片-预设问题'
  | '下载文件'

export interface AiC8146ExposureParams {
  sessionID: string
  /** 因何触发弹窗 */
  trigger: AiC8146Trigger
  /** 当前余额(积分) */
  available: number
  /** 本次查询本来要消耗的积分 */
  cost: number
}

/** ai_b8112_c8146_exposure —— 积分不足弹窗曝光 */
export const trackAiC8146Exposure = (params: AiC8146ExposureParams): void => {
  sensors.track('ai_b8112_c8146_exposure', {
    ...baseFields(TRACK_SIGN_C8146_EXPOSURE),
    sessionID: params.sessionID || '',
    string1: params.trigger,
    number1: params.available ?? 0,
    number2: params.cost ?? 0
  })
}

export type AiC8146ClickItem =
  | '联系客服'
  | '我知道了'
  | '开通会员'
  | '增购积分'
  | '关闭弹窗'

export interface AiC8146ClickParams {
  sessionID: string
  clickItem: AiC8146ClickItem
  trigger: AiC8146Trigger
  available: number
  cost: number
}

/** ai_b8112_c8146_click —— 积分不足弹窗点击 */
export const trackAiC8146Click = (params: AiC8146ClickParams): void => {
  sensors.track('ai_b8112_c8146_click', {
    ...baseFields(TRACK_SIGN_C8146_CLICK),
    sessionID: params.sessionID || '',
    clickItem: params.clickItem,
    string1: params.trigger,
    number1: params.available ?? 0,
    number2: params.cost ?? 0
  })
}

// ==================== 工具: 从 progressSteps 提取 list1 / number1 ====================

/**
 * 把 progressSteps 聚合成 ai_b8112_completed 的 list1 + number1。
 *   list1: 按调用次数从高到低的工具名列表 (去重排序)
 *   number1: 工具调用总次数 (含重复)
 */
export const aggregateToolCalls = (
  steps: Array<{ tool_name?: string }> | undefined
): { list1: string[]; number1: number } => {
  if (!steps || steps.length === 0) return { list1: [], number1: 0 }
  const counter = new Map<string, number>()
  for (const s of steps) {
    const name = (s.tool_name || '').trim()
    if (!name) continue
    counter.set(name, (counter.get(name) || 0) + 1)
  }
  const list1 = Array.from(counter.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([name]) => name)
  return { list1, number1: steps.length }
}

// ============ hover 快捷卡片 (rid=1121, positionId b4343/c8144) ============
//
// AI 问照片入口按钮 hover 弹出的快捷卡片 (AiQuickAskCard) 专属埋点。
// 跟主面板 ai_b8112_* 不同命名空间: 走 workgroup_b4343_c8144_*。

const REQUIREMENT_ID_C8144 = 1121
const TRACK_SIGN_C8144_EXPOSURE =
  'aworkgroup.b4343.c8144.exposure.workgroup_b4343_c8144_exposure'
const TRACK_SIGN_C8144_CLICK =
  'aworkgroup.b4343.c8144.click.workgroup_b4343_c8144_click'

/** hover 卡片 clickItem 枚举 (rid=1121 spec, 严格只有这两个值) */
export type HoverCardClickItem = '开始对话' | '点击预设问题'

/**
 * workgroup_b4343_c8144_exposure — hover 快捷卡片曝光。卡片展示时上报一次。
 * @param presets 卡片当前展示的预设问题完整列表 (list1; 后端下发或内置兜底)
 */
export const trackHoverCardExposure = (presets: readonly string[]): void => {
  sensors.track('workgroup_b4343_c8144_exposure', {
    groupID: getGroupID(),
    groupVipLevel: getVipLevel(),
    track_sign: TRACK_SIGN_C8144_EXPOSURE,
    requirement_id: REQUIREMENT_ID_C8144,
    list1: presets
  })
}

/**
 * workgroup_b4343_c8144_click — hover 快捷卡片点击。
 * @param clickItem '开始对话' | '点击预设问题'
 * @param presets   卡片当前展示的预设问题完整列表 (list1)
 * @param index     clickItem='点击预设问题' 时第几条 (1-based, number1); '开始对话' 传 0
 * @param question  clickItem='点击预设问题' 时的具体文本 (string1); '开始对话' 传 ''
 */
export const trackHoverCardClick = (params: {
  clickItem: HoverCardClickItem
  presets: readonly string[]
  index?: number
  question?: string
}): void => {
  sensors.track('workgroup_b4343_c8144_click', {
    groupID: getGroupID(),
    groupVipLevel: getVipLevel(),
    track_sign: TRACK_SIGN_C8144_CLICK,
    requirement_id: REQUIREMENT_ID_C8144,
    clickItem: params.clickItem,
    list1: params.presets,
    number1: params.index ?? 0,
    string1: params.question ?? ''
  })
}

// ============ c8147 内测申请弹窗 (2026-06-11 spec, 替代前端占位 ai_b8132_*) ============
//
// AI 问照片「内测申请」付费弹窗(选档位 → 内测专享价 → 立即申请下单支付)。
// 支付成功的转化由服务端埋点 ServerOrderDataSyncSuc4(source_detail=the_king_of_AI_photo)
// 与 xiaohei-claw beta_order 表覆盖, 平台未登记前端 paid 事件, 不再上报。

/** 档位 key → 平台 string1 中文枚举 (16032/16033 rule 原文) */
const BETA_PLAN_LABELS: Record<string, string> = {
  '5000': '5000张试用',
  '10000': '1万张半年',
  '30000': '3万张一年',
  '100000': '10万张二年'
}

const betaPlanLabel = (planKey?: string): string =>
  (planKey && BETA_PLAN_LABELS[planKey]) || planKey || ''

export interface AiBetaExposureParams {
  /** 当前会话 ID */
  sessionID?: string
  /** 曝光时后端返回的剩余名额 */
  remaining?: number
  /** 默认选中的档位 key(如 '10000', 内部映射成中文枚举上报) */
  planKey?: string
}

/** ai_b8112_c8147_exposure —— 内测申请弹窗曝光, 弹窗打开时上报一次 */
export const trackAiBetaExposure = (params: AiBetaExposureParams): void => {
  sensors.track('ai_b8112_c8147_exposure', {
    ...baseFields(TRACK_SIGN_C8147_EXPOSURE),
    sessionID: params.sessionID ?? '',
    number1: params.remaining ?? 0,
    string1: betaPlanLabel(params.planKey)
  })
}

/** 内测弹窗 clickItem 枚举 (16033 rule: 切换档位,联系客服咨询,立即申请,关闭弹窗) */
export type AiBetaClickItem =
  | '切换档位'
  | '立即申请'
  | '联系客服咨询'
  | '关闭弹窗'

export interface AiBetaClickParams {
  clickItem: AiBetaClickItem
  /** 当前会话 ID */
  sessionID?: string
  /** 当前选中档位 key(内部映射成中文枚举上报) */
  planKey?: string
  /** 剩余名额 */
  remaining?: number
}

/** ai_b8112_c8147_click —— 内测申请弹窗内的点击行为 */
export const trackAiBetaClick = (params: AiBetaClickParams): void => {
  sensors.track('ai_b8112_c8147_click', {
    ...baseFields(TRACK_SIGN_C8147_CLICK),
    clickItem: params.clickItem,
    sessionID: params.sessionID ?? '',
    string1: betaPlanLabel(params.planKey),
    number1: params.remaining ?? 0
  })
}

// ============ c8132 下载与生成任务二级页 (2026-06-11 spec) ============

export interface AiC8132ExposureParams {
  sessionID: string
  /** 任务总数 */
  total: number
  /** 生成中任务数 */
  pending: number
  /** 已就绪任务数 */
  done: number
  /** 失败任务数 */
  failed: number
}

/** ai_b8112_c8132_exposure —— 下载任务二级页(侧栏)打开曝光 */
export const trackAiC8132Exposure = (params: AiC8132ExposureParams): void => {
  sensors.track('ai_b8112_c8132_exposure', {
    ...baseFields(TRACK_SIGN_C8132_EXPOSURE),
    sessionID: params.sessionID || '',
    number1: params.total ?? 0,
    number2: params.pending ?? 0,
    number3: params.done ?? 0,
    number4: params.failed ?? 0
  })
}

export type AiC8132ClickItem =
  | '点击下载链接'
  | '点击原图'
  | '关闭'
  | '点击会员专属任务'

/** 任务文件类型 (16034 task_type rule: excel,pdf,zip,image,csv,word,other) */
export type AiC8132TaskType =
  | 'excel'
  | 'pdf'
  | 'zip'
  | 'image'
  | 'csv'
  | 'word'
  | 'other'

export interface AiC8132ClickParams {
  sessionID: string
  clickItem: AiC8132ClickItem
  /** clickItem=点击下载链接/点击原图/点击会员专属任务 时的任务类型 */
  taskType?: AiC8132TaskType
  /** 第几个任务 (1-based) */
  index?: number
  /** 任务名 */
  fileName?: string
  /** 任务 ID (DownloadTask.task_id) */
  taskID?: string
  /** 任务由哪个 AI 回答产生 (没有关联时传 '') */
  msgID?: string
}

/** ai_b8112_c8132_click —— 下载任务二级页点击 */
export const trackAiC8132Click = (params: AiC8132ClickParams): void => {
  sensors.track('ai_b8112_c8132_click', {
    ...baseFields(TRACK_SIGN_C8132_CLICK),
    sessionID: params.sessionID || '',
    clickItem: params.clickItem,
    task_type: params.taskType ?? 'other',
    number1: params.index ?? 0,
    fileName: params.fileName ?? '',
    taskID: params.taskID ?? '',
    msgID: params.msgID ?? ''
  })
}

/** 文件名/URL → c8132 task_type */
export const fileNameToTaskType = (name: string): AiC8132TaskType => {
  const lower = (name || '').toLowerCase()
  if (/\.xlsx?($|\?)/.test(lower)) return 'excel'
  if (/\.pdf($|\?)/.test(lower)) return 'pdf'
  if (/\.zip($|\?)/.test(lower)) return 'zip'
  if (/\.(jpe?g|png|webp|gif|bmp|heic)($|\?)/.test(lower)) return 'image'
  if (/\.(csv|tsv)($|\?)/.test(lower)) return 'csv'
  if (/\.docx?($|\?)/.test(lower)) return 'word'
  return 'other'
}

// ============ vip_b8148 积分加量包弹窗 (2026-06-11 spec, avip.b8148) ============
//
// PointsPackModal(积分加量包购买弹窗)在 AI 问照片内打开时的专属埋点。
// 套餐名(string1/string2)用 KV 配置的中文名: 标准包/划算包/大容量包/企业包。

/** 加量包弹窗打开来源 (16037/16038 sourceType rule 原文) */
export type VipB8148SourceType =
  | '我的积分弹窗'
  | '会话页面底部积分提示条'
  | '积分不足弹窗'

export interface VipB8148ExposureParams {
  /** 曝光时默认选中的套餐名(标准包/划算包/大容量包/企业包) */
  defaultPack: string
  sourceType: VipB8148SourceType
}

/** vip_b8148_exposure —— 积分加量包弹窗曝光 */
export const trackVipB8148Exposure = (params: VipB8148ExposureParams): void => {
  sensors.track('vip_b8148_exposure', {
    ...baseFields(TRACK_SIGN_B8148_EXPOSURE),
    string1: params.defaultPack || '',
    sourceType: params.sourceType
  })
}

export type VipB8148ClickItem =
  | '切换套餐'
  | '关闭'
  | '切换支付方式'
  | '查看会员协议'
  | '查看自动续费协议'
  | '刷新二维码'
  | '立即支付'

export interface VipB8148ClickParams {
  clickItem: VipB8148ClickItem
  /** 页面曝光时的默认套餐 */
  defaultPack: string
  sourceType: VipB8148SourceType
  /** clickItem=切换套餐 时切换后的套餐; =切换支付方式 时当前选中套餐 */
  pack?: string
  /** clickItem=切换支付方式 时切换到的支付方式 */
  channel?: '微信' | '支付宝'
}

/** vip_b8148_click —— 积分加量包弹窗点击 */
export const trackVipB8148Click = (params: VipB8148ClickParams): void => {
  sensors.track('vip_b8148_click', {
    ...baseFields(TRACK_SIGN_B8148_CLICK),
    clickItem: params.clickItem,
    string1: params.defaultPack || '',
    string2: params.pack ?? '',
    string3: params.channel ?? '',
    sourceType: params.sourceType
  })
}
